import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { 
  Mail, Phone, MapPin, Building, Clock, ArrowRight, Check, AlertCircle,
  Send, MessageSquare, Globe, Users, Shield, ExternalLink,
  Linkedin, Twitter, Instagram, Facebook, ChevronRight, Star, Headphones,
  Briefcase, Code, FileText, CheckCircle2
} from 'lucide-react';
import { contactInfo, companyInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Background images
const bgImages = {
  hero: "https://images.unsplash.com/photo-1764079833336-53adf377075b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBjaXR5JTIwc2t5bGluZSUyMG5pZ2h0JTIwbGlnaHRzfGVufDB8fHx8MTc2OTMwOTU4NHww&ixlib=rb-4.1.0&q=85",
  office: "https://images.unsplash.com/photo-1766740606233-6573571caa6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMEluZGlhJTIwY29ycG9yYXRlJTIwaGVhZHF1YXJ0ZXJzfGVufDB8fHx8MTc2OTMwOTYwMHww&ixlib=rb-4.1.0&q=85",
  network: "https://images.unsplash.com/photo-1761807386518-805887e89fad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBjaXR5JTIwc2t5bGluZSUyMG5pZ2h0JTIwbGlnaHRzfGVufDB8fHx8MTc2OTMwOTU4NHww&ixlib=rb-4.1.0&q=85"
};

const subjectOptions = [
  "General Corporate Enquiry",
  "Recruitment",
  "Technology Partnership",
  "Vendor Registration",
  "Media & Press",
  "Compliance / Regulatory",
  "Business Proposal",
  "Other"
];

const ContactUltra = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    organisation_name: '',
    email: '',
    subject: 'General Corporate Enquiry',
    message: '',
    confirmation_check: false,
    website: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.full_name.trim()) {
      setError('Full Name is required.');
      return false;
    }
    if (!formData.organisation_name.trim()) {
      setError('Organisation Name is required.');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Official Email Address is required.');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid official email address.');
      return false;
    }
    if (!formData.subject) {
      setError('Please select a subject for your enquiry.');
      return false;
    }
    if (!formData.message.trim()) {
      setError('Message is required.');
      return false;
    }
    if (formData.message.trim().length < 10) {
      setError('Message must be at least 10 characters.');
      return false;
    }
    if (!formData.confirmation_check) {
      setError('Please confirm that this communication relates to official business with Quanentry Capital Private Limited.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      let recaptchaToken = '';
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('contact_submit');
        } catch (e) {
          // ignore
        }
      }

      const res = await axios.post(`${API}/contact/submit`, {
        ...formData,
        recaptcha_token: recaptchaToken
      });

      setSubmittedData(res.data);
      setSubmitted(true);
      
      setFormData({
        full_name: '',
        organisation_name: '',
        email: '',
        subject: 'General Corporate Enquiry',
        message: '',
        confirmation_check: false,
        website: ''
      });
      
    } catch (err) {
      console.error('Contact submission failed:', err);
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail)
        ? (detail[0]?.msg || 'Please check the form fields and try again.')
        : (typeof detail === 'string' ? detail : 'Submission failed. Please try again.');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Contact info items
  const contactItems = [
    { 
      icon: MapPin, 
      label: contactInfo.registeredOffice.label, 
      value: contactInfo.registeredOffice.value,
      color: "#667eea",
      link: null
    },
    { 
      icon: Building, 
      label: contactInfo.cin.label, 
      value: contactInfo.cin.value,
      color: "#f5576c",
      link: null
    },
    { 
      icon: Mail, 
      label: contactInfo.email.label, 
      value: contactInfo.email.value,
      color: "#4facfe",
      link: `mailto:${contactInfo.email.value}`
    },
    { 
      icon: Phone, 
      label: contactInfo.phone.label, 
      value: contactInfo.phone.value,
      color: "#43e97b",
      link: `tel:${contactInfo.phone.value}`
    },
    { 
      icon: Clock, 
      label: "BUSINESS HOURS", 
      value: "Monday – Friday\n10:00 AM – 6:00 PM IST",
      color: "#fa709a",
      link: null
    }
  ];

  // Social media links
  const socialLinks = [
    { icon: Linkedin, url: companyInfo.socialMedia.linkedin, label: "LinkedIn" },
    { icon: Twitter, url: companyInfo.socialMedia.twitter, label: "Twitter" },
    { icon: Instagram, url: companyInfo.socialMedia.instagram, label: "Instagram" },
    { icon: Facebook, url: companyInfo.socialMedia.facebook, label: "Facebook" }
  ];

  // Success Confirmation View
  if (submitted) {
    return (
      <div className="page-container" data-testid="contact-success-page">
        <section className="contact-success-ultra" style={{ padding: '80px 20px', minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="success-cosmic-bg">
            <div className="success-orb orb-1"></div>
            <div className="success-orb orb-2"></div>
          </div>
          
          <motion.div 
            className="success-container-ultra"
            style={{ maxWidth: '720px', width: '100%', background: '#0d1527', border: '1px solid rgba(0, 255, 209, 0.25)', borderRadius: '16px', padding: '40px', boxShadow: '0 20px 60px rgba(0,0,0,0.6)', textAlignment: 'center' }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div 
              className="success-icon-wrapper"
              style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(0, 255, 209, 0.1)', border: '2px solid #00ffd1', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', color: '#00ffd1' }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', bounce: 0.5 }}
            >
              <CheckCircle2 size={48} />
            </motion.div>

            <motion.h1 
              className="display-medium"
              style={{ color: '#ffffff', fontSize: '28px', fontWeight: '700', marginBottom: '12px', textAlign: 'center' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Corporate Enquiry Received
            </motion.h1>

            <motion.p 
              className="body-large"
              style={{ color: '#94a3b8', fontSize: '15px', lineHeight: '1.6', textAlign: 'center', marginBottom: '28px' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Thank you for contacting Quanentry Capital Private Limited. Your official communication has been registered in our institutional records.
            </motion.p>

            {/* Reference Number Box */}
            <motion.div 
              style={{ background: 'rgba(0, 255, 209, 0.05)', border: '1px solid rgba(0, 255, 209, 0.3)', borderRadius: '12px', padding: '24px', marginBottom: '28px', textAlign: 'left' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#00ffd1', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                Enquiry Reference Number
              </div>
              <div style={{ fontFamily: 'Monaco, Consolas, monospace', fontSize: '22px', fontWeight: '800', color: '#ffffff', letterSpacing: '1px', marginBottom: '16px' }}>
                {submittedData?.reference_number || 'QC-CON-20260730-000001'}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', fontSize: '13.5px' }}>
                <div>
                  <span style={{ color: '#64748b', display: 'block', fontSize: '11px', textTransform: 'uppercase' }}>Full Name</span>
                  <span style={{ color: '#f8fafc', fontWeight: '600' }}>{submittedData?.full_name}</span>
                </div>
                <div>
                  <span style={{ color: '#64748b', display: 'block', fontSize: '11px', textTransform: 'uppercase' }}>Organisation</span>
                  <span style={{ color: '#f8fafc', fontWeight: '600' }}>{submittedData?.organisation_name}</span>
                </div>
                <div>
                  <span style={{ color: '#64748b', display: 'block', fontSize: '11px', textTransform: 'uppercase' }}>Subject / Category</span>
                  <span style={{ color: '#f8fafc', fontWeight: '600' }}>{submittedData?.subject}</span>
                </div>
                <div>
                  <span style={{ color: '#64748b', display: 'block', fontSize: '11px', textTransform: 'uppercase' }}>Submission Time</span>
                  <span style={{ color: '#f8fafc', fontWeight: '600' }}>{submittedData?.date} {submittedData?.time}</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              style={{ background: 'rgba(15, 23, 42, 0.6)', borderLeft: '3px solid #00ffd1', padding: '16px 20px', borderRadius: '6px', textAlign: 'left', marginBottom: '32px', fontSize: '13.5px', color: '#cbd5e1', lineHeight: '1.6' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <p style={{ margin: 0 }}>
                An automated confirmation email containing your Reference Number has been dispatched to <strong style={{ color: '#ffffff' }}>{submittedData?.email}</strong>.
              </p>
              <p style={{ margin: '14px 0 0 0', color: '#00ffd1', fontWeight: '600', fontSize: '14px', lineHeight: '1.6' }}>
                Please check your Inbox and Spam/Junk folder for your Enquiry Reference Number if you do not receive the email within a few minutes.
              </p>
              <p style={{ margin: '12px 0 0 0' }}>
                An appropriate team member will review your communication. Expected response time: <strong style={{ color: '#00ffd1' }}>Within two business days</strong>.
              </p>
            </motion.div>

            <motion.button 
              className="btn-primary btn-mega"
              style={{ width: '100%', padding: '14px 28px', fontSize: '15px', fontWeight: '700', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              onClick={() => {
                setSubmitted(false);
                setSubmittedData(null);
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="send-another-btn"
            >
              Submit Another Corporate Enquiry
              <ArrowRight size={18} />
            </motion.button>
          </motion.div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-container" ref={containerRef} data-testid="contact-page">
      {/* Hero Section with Background Image */}
      <section className="contact-hero-ultra">
        {/* Background Image Layer */}
        <motion.div 
          className="contact-hero-bg-image"
          style={{ 
            backgroundImage: `url(${bgImages.hero})`,
            y,
            scale
          }}
        />
        <div className="contact-hero-overlay" />
        
        <motion.div 
          className="contact-cosmic-bg"
          style={{ opacity }}
        >
          <div className="cosmic-orb orb-mega-1"></div>
          <div className="cosmic-orb orb-mega-2"></div>
          <div className="cosmic-orb orb-mega-3"></div>
          <div className="contact-grid-pattern"></div>
          
          {/* Floating icons */}
          <div className="floating-contact-icons">
            {[Mail, Phone, MessageSquare, Globe, Send].map((Icon, i) => (
              <motion.div
                key={i}
                className="floating-contact-icon"
                style={{
                  left: `${10 + i * 20}%`,
                  top: `${15 + (i % 3) * 30}%`,
                }}
                animate={{
                  y: [0, -25, 0],
                  rotate: [0, 10, -10, 0],
                  opacity: [0.2, 0.5, 0.2],
                }}
                transition={{
                  duration: 5 + i,
                  repeat: Infinity,
                  delay: i * 0.5,
                }}
              >
                <Icon size={36} />
              </motion.div>
            ))}
          </div>
          
          <div className="globe-animation">
            <motion.div 
              className="globe-icon"
              animate={{ rotate: 360 }}
              transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            >
              <Globe size={300} strokeWidth={0.5} />
            </motion.div>
            <div className="globe-glow"></div>
          </div>
        </motion.div>

        <motion.div 
          className="contact-hero-content"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <motion.div
            className="hero-badge-contact"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", bounce: 0.6 }}
          >
            <MessageSquare size={20} />
            Official Portal
          </motion.div>

          <h1 className="display-huge gradient-text-contact">
            Corporate Communications
          </h1>

          <p className="hero-description-contact">
            For official corporate communication, recruitment enquiries, technology partnerships, regulatory correspondence, vendor discussions and business-related enquiries.
          </p>

          <motion.div 
            style={{ margin: '16px 0 24px 0', display: 'flex', justifyContent: 'center' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <a 
              href="mailto:contact@quanentry.com" 
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 20px', background: 'rgba(0, 255, 209, 0.08)', border: '1px solid rgba(0, 255, 209, 0.3)', borderRadius: '20px', color: '#00ffd1', fontWeight: '700', fontSize: '15px', textDecoration: 'none' }}
            >
              <Mail size={18} />
              contact@quanentry.com
            </a>
          </motion.div>

          <motion.div 
            className="contact-quick-links"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <motion.a 
              href="mailto:contact@quanentry.com"
              className="quick-link-btn"
              whileHover={{ y: -5, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Mail size={20} />
              Email Us
            </motion.a>
            <motion.a 
              href="tel:+918178643947"
              className="quick-link-btn"
              whileHover={{ y: -5, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Phone size={20} />
              Call Us
            </motion.a>
          </motion.div>
        </motion.div>
      </section>

      {/* Contact Info & Form Section */}
      <section className="contact-main-ultra">
        <div className="container-ultra">
          <div className="contact-grid-ultra">
            {/* Left Side - Contact Information */}
            <motion.div 
              className="contact-info-ultra"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
              }}
            >
              <motion.div
                className="info-header"
                variants={{
                  hidden: { x: -50, opacity: 0 },
                  visible: { x: 0, opacity: 1 }
                }}
              >
                <span className="section-label">Official Communication</span>
                <h2 className="display-medium">Contact Information</h2>
                <p className="info-subtitle">Quanentry Capital Private Limited</p>
              </motion.div>

              <div className="info-cards-container">
                {contactItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={index}
                      className="info-card-ultra"
                      variants={{
                        hidden: { x: -30, opacity: 0 },
                        visible: { x: 0, opacity: 1 }
                      }}
                      whileHover={{ x: 10, scale: 1.02 }}
                    >
                      <div className="info-icon-wrapper" style={{ background: item.color }}>
                        <Icon size={24} />
                      </div>
                      <div className="info-content">
                        <h4 className="info-label">{item.label}</h4>
                        {item.link ? (
                          <a href={item.link} className="info-value-link">{item.value}</a>
                        ) : (
                          <p className="info-value" style={{ whiteSpace: 'pre-line' }}>{item.value}</p>
                        )}
                      </div>
                      <ChevronRight size={20} className="info-arrow" />
                    </motion.div>
                  );
                })}
              </div>

              {/* Social Links */}
              <motion.div 
                className="social-section-ultra"
                variants={{
                  hidden: { y: 30, opacity: 0 },
                  visible: { y: 0, opacity: 1 }
                }}
              >
                <h4 className="social-title">Connect With Us</h4>
                <div className="social-links-ultra">
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <motion.a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link-ultra"
                        whileHover={{ y: -5, scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title={social.label}
                      >
                        <Icon size={22} />
                      </motion.a>
                    );
                  })}
                </div>
              </motion.div>
            </motion.div>

            {/* Right Side - Official Corporate Contact Form */}
            <motion.div 
              className="contact-form-ultra"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="form-container-ultra">
                <div className="form-header">
                  <Send size={32} className="form-icon" />
                  <h2 className="heading-1">Submit Corporate Enquiry</h2>
                  <p className="form-subtitle">Please complete the form below for official communication with Quanentry Capital Private Limited.</p>
                </div>

                <AnimatePresence>
                  {error && (
                    <motion.div 
                      className="error-message-ultra"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      data-testid="form-error"
                    >
                      <AlertCircle size={24} />
                      <span>{error}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                <form onSubmit={handleSubmit} className="form-ultra" data-testid="contact-form">
                  {/* Honeypot field */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={formData.website}
                    onChange={handleInputChange}
                    style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
                    data-testid="contact-honeypot"
                  />

                  {/* Full Name */}
                  <div className={`form-group-ultra ${focusedField === 'full_name' ? 'focused' : ''}`}>
                    <label htmlFor="full_name" className="form-label-ultra">
                      Full Name <span className="required">*</span>
                    </label>
                    <motion.input
                      whileFocus={{ scale: 1.005 }}
                      type="text"
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('full_name')}
                      onBlur={() => setFocusedField(null)}
                      className="form-input-ultra"
                      placeholder="e.g. Rahul Sharma"
                      data-testid="input-full-name"
                    />
                  </div>

                  {/* Organisation Name */}
                  <div className={`form-group-ultra ${focusedField === 'organisation_name' ? 'focused' : ''}`}>
                    <label htmlFor="organisation_name" className="form-label-ultra">
                      Organisation Name <span className="required">*</span>
                    </label>
                    <motion.input
                      whileFocus={{ scale: 1.005 }}
                      type="text"
                      id="organisation_name"
                      name="organisation_name"
                      value={formData.organisation_name}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('organisation_name')}
                      onBlur={() => setFocusedField(null)}
                      className="form-input-ultra"
                      placeholder="e.g. Acme Capital Partners Ltd."
                      data-testid="input-organisation-name"
                    />
                  </div>

                  {/* Official Email Address */}
                  <div className={`form-group-ultra ${focusedField === 'email' ? 'focused' : ''}`}>
                    <label htmlFor="email" className="form-label-ultra">
                      Official Email Address <span className="required">*</span>
                    </label>
                    <motion.input
                      whileFocus={{ scale: 1.005 }}
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      className="form-input-ultra"
                      placeholder="e.g. name@company.com"
                      data-testid="input-email"
                    />
                  </div>

                  {/* Subject Dropdown */}
                  <div className={`form-group-ultra ${focusedField === 'subject' ? 'focused' : ''}`}>
                    <label htmlFor="subject" className="form-label-ultra">
                      Subject <span className="required">*</span>
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('subject')}
                      onBlur={() => setFocusedField(null)}
                      className="form-select-ultra"
                      data-testid="select-subject"
                    >
                      {subjectOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  {/* Message Textarea */}
                  <div className={`form-group-ultra ${focusedField === 'message' ? 'focused' : ''}`}>
                    <label htmlFor="message" className="form-label-ultra">
                      Message <span className="required">*</span>
                    </label>
                    <motion.textarea
                      whileFocus={{ scale: 1.005 }}
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField(null)}
                      className="form-textarea-ultra"
                      placeholder="Please detail the nature of your corporate inquiry or discussion..."
                      rows="5"
                      data-testid="input-message"
                    />
                  </div>

                  {/* Confirmation Checkbox */}
                  <div className="form-group-ultra" style={{ marginTop: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13.5px', color: '#cbd5e1', lineHeight: '1.5' }}>
                      <input
                        type="checkbox"
                        id="confirmation_check"
                        name="confirmation_check"
                        checked={formData.confirmation_check}
                        onChange={handleInputChange}
                        style={{ width: '18px', height: '18px', accentColor: '#00ffd1', marginTop: '2px', flexShrink: 0, cursor: 'pointer' }}
                        data-testid="checkbox-confirmation"
                      />
                      <span>I confirm that this communication relates to official business with Quanentry Capital Private Limited. <span className="required">*</span></span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    className="btn-primary btn-mega submit-btn-ultra"
                    disabled={loading}
                    whileHover={{ scale: loading ? 1 : 1.02, boxShadow: "0 20px 60px rgba(0, 255, 209, 0.3)" }}
                    whileTap={{ scale: loading ? 1 : 0.98 }}
                    style={{ marginTop: '24px' }}
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <>
                        <span className="loading-spinner"></span>
                        Processing Communication...
                      </>
                    ) : (
                      <>
                        Submit Corporate Enquiry
                        <Send size={20} />
                      </>
                    )}
                  </motion.button>
                </form>

                {/* Security Notice */}
                <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontSize: '12.5px', lineHeight: '1.4' }}>
                  <Shield size={16} style={{ color: '#00ffd1', flexShrink: 0 }} />
                  <span>Your information is transmitted securely and handled in accordance with Quanentry Capital's Privacy Policy.</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Escalation Matrix Section */}
      <section className="escalation-ultra-section">
        <div className="container-ultra">
          <motion.div
            className="section-header-ultra"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label-center">Governance</span>
            <h2 className="display-medium">Escalate With Confidence</h2>
            <p className="section-subtitle">
              If your official communication remains unresolved after following Quanentry Capital's internal communication process, you may approach the appropriate regulatory authority where applicable.
            </p>
          </motion.div>

          <div className="escalation-grid-ultra">
            {/* CEO Direct Contact Card */}
            <motion.div 
              className="ceo-card-ultra"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="ceo-glow"></div>
              
              <div className="ceo-header">
                <div className="ceo-avatar">
                  <Users size={48} />
                </div>
                <div className="ceo-title-section">
                  <h3 className="heading-2">Executive Contact</h3>
                  <span className="ceo-badge">Founder & CEO</span>
                </div>
              </div>

              <div className="ceo-details-ultra">
                <motion.div 
                  className="ceo-detail-item"
                  whileHover={{ x: 5 }}
                >
                  <Users size={20} />
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{contactInfo.escalationMatrix.ceo.name}</span>
                </motion.div>

                <motion.div 
                  className="ceo-detail-item"
                  whileHover={{ x: 5 }}
                >
                  <Mail size={20} />
                  <span className="detail-label">CEO Email:</span>
                  <a href={`mailto:${contactInfo.escalationMatrix.ceo.email}`} className="detail-link">
                    {contactInfo.escalationMatrix.ceo.email}
                  </a>
                </motion.div>

                <motion.div 
                  className="ceo-detail-item"
                  whileHover={{ x: 5 }}
                >
                  <Phone size={20} />
                  <span className="detail-label">Contact Number:</span>
                  <a href={`tel:${contactInfo.escalationMatrix.ceo.phone}`} className="detail-link">
                    {contactInfo.escalationMatrix.ceo.phone}
                  </a>
                </motion.div>

                <motion.div 
                  className="ceo-detail-item availability"
                  whileHover={{ x: 5 }}
                >
                  <Clock size={20} />
                  <span className="detail-value">{contactInfo.escalationMatrix.availability}</span>
                </motion.div>
              </div>
            </motion.div>

            {/* Regulatory Escalation Card */}
            <motion.div 
              className="regulatory-card-ultra"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="regulatory-header">
                <Headphones size={40} />
                <h3 className="heading-2">Escalate With Confidence</h3>
              </div>

              <p className="regulatory-description">
                If your official communication remains unresolved after following Quanentry Capital's internal communication process, you may approach the appropriate regulatory authority where applicable.
              </p>

              <div className="regulatory-links-ultra">
                {contactInfo.escalationMatrix.regulatoryBodies.map((body, index) => (
                  <motion.a
                    key={index}
                    href={body.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="regulatory-link-ultra"
                    whileHover={{ y: -5, scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>{body.name}</span>
                    <ExternalLink size={16} />
                  </motion.a>
                ))}
              </div>

              <div className="regulatory-note-ultra">
                <Star size={18} />
                <p>Please retain your Quanentry Service Ticket or Enquiry Reference Number before approaching any regulatory authority. This assists in faster review and correspondence.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map/Location Section with Embedded Google Map */}
      <section className="location-ultra-section">
        <div className="location-bg-image" style={{ backgroundImage: `url(${bgImages.office})` }} />
        <div className="location-overlay" />
        
        <div className="location-bg">
          <div className="location-orb orb-1"></div>
          <div className="location-orb orb-2"></div>
        </div>
        
        <motion.div
          className="location-content"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ maxWidth: '960px', margin: '0 auto' }}
        >
          <motion.div 
            className="location-icon-wrapper"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <MapPin size={56} />
          </motion.div>
          <h2 className="display-medium">Visit Our Office</h2>
          <p className="location-address">{contactInfo.registeredOffice.value}</p>
          
          <div className="location-features">
            {[
              { icon: <Clock size={20} />, text: "Mon-Fri: 10:00 AM - 6:00 PM IST" },
              { icon: <Phone size={20} />, text: companyInfo.phone },
              { icon: <Mail size={20} />, text: companyInfo.email }
            ].map((feature, i) => (
              <motion.div 
                key={i}
                className="location-feature"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                {feature.icon}
                <span>{feature.text}</span>
              </motion.div>
            ))}
          </div>

          {/* Embedded Google Maps Frame */}
          <div style={{ marginTop: '28px', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(0, 255, 209, 0.3)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
            <iframe 
              title="Quanentry Capital Registered Office Location" 
              src="https://maps.google.com/maps?q=944%20Block%20C%20Sushant%20Lok%20Phase%201%20Gurugram%20Haryana%20122001&t=&z=15&ie=UTF8&iwloc=&output=embed" 
              width="100%" 
              height="320" 
              style={{ border: 0, display: 'block' }} 
              allowFullScreen="" 
              loading="lazy"
            />
          </div>
          
          <motion.a 
            href={`https://maps.google.com/?q=${encodeURIComponent(contactInfo.registeredOffice.value)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary btn-mega"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ marginTop: '24px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
          >
            Get Directions
            <ExternalLink size={20} />
          </motion.a>
        </motion.div>
      </section>
      
      {/* Why Reach Out Section */}
      <section className="why-contact-section">
        <div className="container-ultra">
          <motion.div
            className="section-header-ultra"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label-center">Why Reach Out</span>
            <h2 className="display-medium">Purpose of Engagement</h2>
          </motion.div>
          
          <div className="why-contact-grid">
            {[
              { 
                icon: <Mail size={28} />, 
                title: "Corporate Communication", 
                desc: "Official business correspondence and general corporate enquiries." 
              },
              { 
                icon: <Briefcase size={28} />, 
                title: "Recruitment", 
                desc: "Career-related communication and recruitment enquiries." 
              },
              { 
                icon: <Code size={28} />, 
                title: "Technology Partnerships", 
                desc: "Technology collaborations and infrastructure discussions." 
              },
              { 
                icon: <FileText size={28} />, 
                title: "Regulatory Correspondence", 
                desc: "Official compliance and regulatory communication." 
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                className="why-contact-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -10, scale: 1.03 }}
              >
                <div className="why-contact-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUltra;
